import apiUtil, {
  clearCookieAndRedirectToLogin,
  throwApiError,
} from '~/utils/apiUtils'

const { get, post } = apiUtil()

export interface AuthResult {
  token?: string
  termsAcceptanceRequired?: boolean
  termsVersion?: string
  onboardingRequired?: boolean
  mfaRequired?: boolean
  mfaChallengeToken?: string
  expiresInSeconds?: number
}

export interface SignupResult extends AuthResult {
  message?: string
  emailVerificationRequired?: boolean
  joinedOrg?: boolean
  joinedOrgName?: string
  inviteNotJoinedReason?: string
  user?: { id?: string; email?: string }
}

interface AuthEnvelope {
  data: { value: AuthResult }
}

class SignupLoginAPI {
  constructor() {
    this.signup = this.signup.bind(this)
    this.login = this.login.bind(this)
    this.verifyMFA = this.verifyMFA.bind(this)
    this.logout = this.logout.bind(this)
    this.forgotPassword = this.forgotPassword.bind(this)
    this.resetPassword = this.resetPassword.bind(this)
    this.verifyEmail = this.verifyEmail.bind(this)
    this.resendVerification = this.resendVerification.bind(this)
  }

  async signup(
    email: string,
    password: string,
    options?: {
      firstName?: string
      lastName?: string
      country?: string
      postalCode?: string
      paddleSubscriptionId?: string
      paddleCustomerId?: string
      turnstileToken?: string
      inviteToken?: string
    },
  ): Promise<SignupResult> {
    try {
      const body: Record<string, string | boolean> = {
        email,
        password,
      }
      if (options?.firstName) body.firstName = options.firstName
      if (options?.lastName) body.lastName = options.lastName
      if (options?.country) {
        body.country = options.country
      }
      if (options?.postalCode) {
        body.postalCode = options.postalCode
      }
      if (options?.paddleSubscriptionId) {
        body.paddleSubscriptionId = options.paddleSubscriptionId
      }
      if (options?.paddleCustomerId) {
        body.paddleCustomerId = options.paddleCustomerId
      }
      if (options?.turnstileToken) {
        body.turnstileToken = options.turnstileToken
      }
      if (options?.inviteToken) {
        body.inviteToken = options.inviteToken
      }

      const response = await post('/auth/signup', body)

      const responseData = await response.json().catch(() => ({}))
      if (!response.ok) {
        // Surface the backend's reason (e.g. "already exists", "captcha_failed")
        // so the form can show a meaningful message.
        throw new Error(
          responseData.error || `HTTP error! status: ${response.status}`,
        )
      }
      return responseData
    } catch (error) {
      console.error('Error signing up:', error)
      throw error
    }
  }

  async acceptTerms(termsVersion: string): Promise<{ message: string }> {
    const response = await post('/auth/terms/accept', { termsVersion })
    if (!response.ok) await throwApiError(response)
    return response.json()
  }

  async getTermsStatus(): Promise<{ accepted: boolean; termsVersion: string }> {
    const response = await get('/auth/terms/status')
    if (!response.ok) await throwApiError(response)
    return response.json()
  }

  // Look up an org invite by its token so the signup page can show the
  // inviting organization and lock the invited email address.
  async getInviteDetails(token: string): Promise<{
    valid: boolean
    reason?: string
    orgName?: string
    email?: string
    role?: string
  }> {
    const response = await get(`/auth/invite/${encodeURIComponent(token)}`)
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      // 404 means unknown token — normalize to the same shape.
      if (response.status === 404) return { valid: false, reason: 'not_found' }
      throw new Error(data.error || `HTTP error! status: ${response.status}`)
    }
    return data
  }

  // POST method to log in
  async login(email: string, password: string): Promise<AuthEnvelope> {
    try {
      // app.baseURL is '/app/' in prod, '/' in dev. The Nuxt server route is served
      // under that base, and prod ingress routes '/api/*' to the backend — so we must
      // address it via the base path (e.g. '/app/api/login'), not a root-absolute path.
      const { app } = useRuntimeConfig()
      const response = await $fetch<AuthResult>(`${app.baseURL}api/login`, {
        method: 'POST',
        body: { email, password },
      })

      return { data: { value: response } }
    } catch (error) {
      console.error('Error logging in:', error)
      throw error
    }
  }

  async verifyMFA(challengeToken: string, code: string): Promise<AuthEnvelope> {
    const { app } = useRuntimeConfig()
    const response = await $fetch<AuthResult>(`${app.baseURL}api/mfa-verify`, {
      method: 'POST',
      body: { challengeToken, code },
    })
    return { data: { value: response } }
  }

  // POST method to log out
  async logout(): Promise<Record<string, unknown> | undefined> {
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      const request = async () => {
        const response = await post(
          '/auth/signout',
          {},
          {
            signal: controller.signal,
            suppressResponseRedirects: true,
          },
        )
        if (!response.ok) await throwApiError(response)
        return await response.json()
      }
      // Bound the full response, including its body. A rejected timeout must
      // settle logout even if the network never resolves or honors abort.
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller.abort()
          reject(new Error('Timed out signing out'))
        }, 2000)
      })
      return await Promise.race([request(), timeout])
    } catch (error) {
      console.error('Error logging out:', error)
    } finally {
      clearTimeout(timer)
      await clearCookieAndRedirectToLogin()
    }
  }

  // POST method for forgot password
  async forgotPassword(
    email: string,
    turnstileToken: string,
  ): Promise<{ message: string }> {
    try {
      const response = await post(
        '/auth/forgot-password',
        { email, turnstileToken },
        { isPublic: true },
      )

      const responseData = await response.json().catch(() => {
        if (!response.ok)
          throw new Error(
            `HTTP error! status: ${response.status} - Failed to parse error response.`,
          )
        return {
          message: 'Request processed, but no specific message returned.',
        }
      })

      if (!response.ok) {
        throw new Error(
          responseData.message || `HTTP error! status: ${response.status}`,
        )
      }

      return {
        message:
          responseData.message ||
          'If your email is registered, you will receive a password reset link.',
      }
    } catch (error) {
      console.error('Error in forgotPassword API call:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error(
        'An unexpected error occurred during the forgot password process.',
      )
    }
  }

  // POST method for resetting password
  async resetPassword(
    token: string,
    newPasswordInput: string,
    confirmPasswordInput: string,
  ): Promise<{ message: string }> {
    try {
      const response = await post(
        '/auth/reset-password',
        {
          token: token,
          password: newPasswordInput,
          passwordConfirm: confirmPasswordInput,
        },
        { isPublic: true },
      )

      const responseData = await response.json().catch(() => {
        if (!response.ok)
          throw new Error(
            `HTTP error! status: ${response.status} - Failed to parse error response.`,
          )
        return {
          message: 'Request processed, but no specific message returned.',
        }
      })

      if (!response.ok) {
        throw new Error(
          responseData.error ||
            responseData.message ||
            `HTTP error! status: ${response.status}`,
        )
      }

      return {
        message:
          responseData.message || 'Password has been reset successfully.',
      }
    } catch (error) {
      console.error('Error in resetPassword API call:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error(
        'An unexpected error occurred during the password reset process.',
      )
    }
  }

  async verifyEmail(
    token: string,
  ): Promise<{ message: string; user: Record<string, unknown> }> {
    try {
      const response = await post(
        '/auth/verify-email',
        { token },
        { isPublic: true },
      )

      const responseData = await response.json().catch(() => {
        if (!response.ok)
          throw new Error(
            `HTTP error! status: ${response.status} - Failed to parse error response.`,
          )
        return {
          message: 'Request processed, but no specific message returned.',
        }
      })

      if (!response.ok) {
        throw new Error(
          responseData.error ||
            responseData.message ||
            `HTTP error! status: ${response.status}`,
        )
      }

      return {
        message: responseData.message || 'Email verified successfully.',
        user: responseData.user,
      }
    } catch (error) {
      console.error('Error in verifyEmail API call:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('An unexpected error occurred during email verification.')
    }
  }

  async resendVerification(email: string): Promise<{ message: string }> {
    try {
      const response = await post(
        '/auth/resend-verification',
        { email },
        { isPublic: true },
      )

      const responseData = await response.json().catch(() => {
        if (!response.ok)
          throw new Error(
            `HTTP error! status: ${response.status} - Failed to parse error response.`,
          )
        return {
          message: 'Request processed, but no specific message returned.',
        }
      })

      if (!response.ok) {
        throw new Error(
          responseData.error ||
            responseData.message ||
            `HTTP error! status: ${response.status}`,
        )
      }

      return {
        message:
          responseData.message ||
          'Verification email sent successfully. Please check your inbox.',
      }
    } catch (error) {
      console.error('Error in resendVerification API call:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error(
        'An unexpected error occurred while resending verification email.',
      )
    }
  }
}

const signupLoginAPI = new SignupLoginAPI()

export default signupLoginAPI
